import { Box, Button, IconButton, FormControlLabel, Checkbox, FormGroup, Typography, useTheme } from "@mui/material"
import {
    OverrideSchedule,
    DayOfWeek,
    DailySchedule,
    isDaysOfWeek,
    INITIAL_STATE_ARMED,
} from "../../../api/AutoArmConfig"
import { ScheduleSetter } from "./ScheduleSetter"
import DeleteIcon from "@mui/icons-material/Delete"
import { useTranslation } from "react-i18next"
import { useCallback, useEffect, useMemo, useState } from "react"
import AddIcon from "@mui/icons-material/Add"

type Props = {
    value: OverrideSchedule[]
    readOnly: boolean
    onChange: (overrides: OverrideSchedule[]) => void
    onError: (hasError: boolean) => void
}

const allDays: DayOfWeek[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]

export const AutoArmWeekly = ({ value, readOnly, onChange, onError }: Props) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const [errorStates, setErrorStates] = useState<boolean[]>(new Array(value.length).fill(false))

    const usedDays = useMemo(() => {
        return new Set(value.flatMap((o) => (isDaysOfWeek(o.condition) ? o.condition.days.days : [])))
    }, [value])

    const availableDays = useMemo(() => {
        return allDays.filter((day) => !usedDays.has(day))
    }, [usedDays])

    const handleAdd = () => {
        if (availableDays.length === 0) return

        const newOverride: OverrideSchedule = {
            condition: { days: { days: [availableDays[0]] } },
            schedule: { initial: INITIAL_STATE_ARMED, transitions: [] },
        }

        setErrorStates((prev) => [...prev, false])
        onChange([...value, newOverride])
    }

    const handleDayToggle = (index: number, toggledDay: DayOfWeek) => {
        const updated = [...value]
        const override = updated[index]

        if (!isDaysOfWeek(override.condition)) return

        const selectedDays = new Set(override.condition.days.days)
        if (selectedDays.has(toggledDay)) {
            selectedDays.delete(toggledDay)
        } else {
            selectedDays.add(toggledDay)
        }

        if (selectedDays.size === 0) return

        updated[index] = {
            ...override,
            condition: { days: { days: Array.from(selectedDays) } },
        }

        onChange(updated)
    }

    const handleScheduleChange = (index: number, newSchedule: DailySchedule) => {
        const updated = [...value]
        updated[index] = {
            ...updated[index],
            schedule: newSchedule,
        }
        onChange(updated)
    }

    const handleRemove = (index: number) => {
        const updated = value.filter((_, i) => i !== index)
        onChange(updated)
        setErrorStates((prev) => prev.filter((_, i) => i !== index))
    }

    const updateErrorState = useCallback(
        (index: number, hasError: boolean) => {
            setErrorStates((prev) => {
                const newErrorStates = [...prev]
                if (newErrorStates[index] !== hasError) {
                    newErrorStates[index] = hasError
                    return newErrorStates
                }
                return prev
            })
        },
        [setErrorStates]
    )

    useEffect(() => {
        onError(errorStates.some((errorState) => errorState))
    }, [errorStates, onError])

    const renderWeeklyOverride = (override: OverrideSchedule, index: number) => {
        if (!isDaysOfWeek(override.condition)) return null
        const selectedDays = override.condition.days.days
        const safeSchedule: DailySchedule = override.schedule ?? { initial: INITIAL_STATE_ARMED, transitions: [] }

        return (
            <Box key={index} mt={2} p={2} border={`1px solid ${theme.palette.grey[400]}`} borderRadius={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle1">{t("action.select_days")}</Typography>
                    <IconButton disabled={readOnly} onClick={() => handleRemove(index)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>

                <FormGroup row>
                    {allDays.map((day) => (
                        <FormControlLabel
                            key={day}
                            control={
                                <Checkbox
                                    checked={selectedDays.includes(day)}
                                    disabled={(!selectedDays.includes(day) && usedDays.has(day)) || readOnly}
                                    onChange={() => handleDayToggle(index, day)}
                                />
                            }
                            label={t(`days.${day.toLowerCase()}`)}
                        />
                    ))}
                </FormGroup>

                <Box mt={2}>
                    <ScheduleSetter
                        value={safeSchedule}
                        readOnly={readOnly}
                        onChange={(newSchedule) => handleScheduleChange(index, newSchedule)}
                        onError={(hasError) => updateErrorState(index, hasError)}
                    />
                </Box>
            </Box>
        )
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {value.map((override, index) => renderWeeklyOverride(override, index))}

            <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAdd}
                disabled={availableDays.length === 0 || readOnly}
            >
                {t("action.add_day")}
            </Button>
        </Box>
    )
}
